import { AlertCircle, Check, Lock, WifiOff, X } from "lucide-react";
import {
  isFulfilledError,
  isClosedError,
  isExpiredError,
  isPaidError,
  isSessionLostError,
} from "../../utils/paymentLinkErrorHandler";
import useTranslation from "../hooks/useTranslation";

const getErrorConfig = (errorCode, t) => {
  if (isFulfilledError(errorCode) || isClosedError(errorCode)) {
    return {
      icon: Lock,
      circleColor: "bg-[#E9FFF3]",
      ringColor: "bg-[#D2FBE5]",
      badgeColor: "bg-singaHijauPrimary",
      title: t("errorTransactionNotFound.fulfilledTitle"),
      message: t("errorTransactionNotFound.fulfilledMessage"),
    };
  }
  if (isExpiredError(errorCode)) {
    return {
      icon: X,
      circleColor: "bg-[#FFDADF]",
      ringColor: "bg-[#FFBAC4]",
      badgeColor: "bg-[#FD5C63]",
      title: t("errorTransactionNotFound.expiredTitle"),
      message: t("errorTransactionNotFound.expiredMessage"),
    };
  }
  if (isPaidError(errorCode)) {
    return {
      icon: Check,
      circleColor: "bg-[#E9FFF3]",
      ringColor: "bg-[#D2FBE5]",
      badgeColor: "bg-singaHijauPrimary",
      title: t("errorTransactionNotFound.paidTitle"),
      message: t("errorTransactionNotFound.paidMessage"),
    };
  }
  if (isSessionLostError(errorCode)) {
    return {
      icon: WifiOff,
      circleColor: "bg-[#E3F1FF]",
      ringColor: "bg-[#C2E2FF]",
      badgeColor: "bg-[#2E90FA]",
      title: t("errorTransactionNotFound.sessionLostTitle"),
      message: t("errorTransactionNotFound.sessionLostMessage"),
    };
  }
  return {
    icon: AlertCircle,
    circleColor: "bg-[#FFF4E5]",
    ringColor: "bg-[#FFE4B8]",
    badgeColor: "bg-[#F5A524]",
    title: t("errorTransactionNotFound.defaultTitle"),
    message: t("errorTransactionNotFound.defaultMessage"),
  };
};

const ErrorTransactionNotFound = ({ errorCode, message }) => {
  const { t } = useTranslation();
  const config = getErrorConfig(errorCode, t);
  const Icon = config.icon;
  // Prefer the polished per-type copy; only fall back to a raw backend message
  // when we don't even recognize the error code (matches previous behavior).
  // const displayMessage = !errorCode && message ? message : config.message;

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#f5f5f5] px-6 py-14">
      <div className="flex w-full max-w-[620px] flex-col items-center gap-7">
        <section className="w-full rounded-[28px] bg-white px-8 py-10 text-center shadow-[0_10px_22px_rgba(15,23,42,0.08)] md:px-12">
          <div className={`mx-auto flex h-32 w-32 items-center justify-center rounded-full ${config.circleColor}`}>
            <div className={`flex h-20 w-20 items-center justify-center rounded-full ${config.ringColor}`}>
              <div className={`flex h-12 w-12 items-center justify-center rounded-full text-white ${config.badgeColor}`}>
                <Icon size={28} strokeWidth={3.5} aria-hidden="true" />
              </div>
            </div>
          </div>

          <h1 className="mt-7 text-2xl font-semibold text-gray-900">{config.title}</h1>
          <p className="mx-auto mt-3 max-w-[500px] text-base font-normal leading-7 text-gray-600">
            {config.message}
          </p>
        </section>

        <div className="flex w-fit items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm">
          <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBy")}</span>
          <img src="/assets/images/logo-singapay-horizontal.png" alt="SingaPay" className="h-6 w-auto object-contain" />
          {t("layout.poweredBySuffix") && (
            <span className="text-sm font-semibold text-gray-900">{t("layout.poweredBySuffix")}</span>
          )}
        </div>
      </div>
    </main>
  );
};

export default ErrorTransactionNotFound;
